import createClient, { type Middleware } from 'openapi-fetch';
import { config } from '@/config/env';
import { idiomaActual } from '@/i18n';
import type { paths } from './schema';
import type { ErrorDto } from './types';

export type TokenProvider = () => Promise<string | null>;

let tokenProvider: TokenProvider = async () => null;
let onNoAutenticat: () => void = () => {};

export function setTokenProvider(fn: TokenProvider): void {
  tokenProvider = fn;
}

export function setOnNoAutenticat(fn: () => void): void {
  onNoAutenticat = fn;
}

export class ApiError extends Error {
  readonly status: number;
  readonly codi: string;
  readonly camp?: string | null;
  readonly detall?: Record<string, unknown> | null;

  constructor(status: number, body: Partial<ErrorDto> | undefined) {
    super(body?.missatge ?? body?.codi ?? `HTTP ${status}`);
    this.name = 'ApiError';
    this.status = status;
    this.codi = body?.codi ?? codiPerStatus(status);
    this.camp = body?.camp;
    this.detall = body?.detall;
  }
}

export function codiPerStatus(status: number): string {
  switch (status) {
    case 0:
      return 'error.xarxa';
    case 401:
      return 'error.no.autenticat';
    case 403:
      return 'error.permis.denegat';
    case 404:
      return 'error.recurs.no.trobat';
    case 412:
      return 'error.etag.no.coincideix';
    case 428:
      return 'error.etag.obligatori';
    case 503:
      return 'error.integracio.no.disponible';
    default:
      return 'error.desconegut';
  }
}

async function capcaleres(extra?: HeadersInit): Promise<Headers> {
  const headers = new Headers(extra);
  const token = await tokenProvider();
  if (token) headers.set('Authorization', `Bearer ${token}`);
  headers.set('Accept-Language', idiomaActual());
  if (!headers.has('Accept')) headers.set('Accept', 'application/json');
  return headers;
}

export const authMiddleware: Middleware = {
  async onRequest({ request }) {
    const headers = await capcaleres(request.headers);
    headers.forEach((valor, clau) => request.headers.set(clau, valor));
    return request;
  },
  async onResponse({ response }) {
    if (response.status === 401) onNoAutenticat();
    return response;
  },
};

export function creaClient(baseUrl: string = config.apiBaseUrl) {
  const client = createClient<paths>({ baseUrl });
  client.use(authMiddleware);
  return client;
}

export const api = creaClient();

interface ResultatFetch {
  data?: unknown;
  error?: unknown;
  response: Response;
}

export async function unwrap<T>(peticio: Promise<ResultatFetch>): Promise<T> {
  let resultat: ResultatFetch;
  try {
    resultat = await peticio;
  } catch {
    throw new ApiError(0, undefined);
  }
  if (!resultat.response.ok) {
    throw new ApiError(resultat.response.status, resultat.error as Partial<ErrorDto> | undefined);
  }
  return resultat.data as T;
}

export async function unwrapAmbEtag<T>(
  peticio: Promise<ResultatFetch>,
): Promise<{ data: T; etag: string | null }> {
  const resultat = await peticio.catch(() => {
    throw new ApiError(0, undefined);
  });
  const data = await unwrap<T>(Promise.resolve(resultat));
  return { data, etag: resultat.response.headers.get('ETag') };
}

export async function apiRequest<T>(
  path: string,
  init: RequestInit & { json?: unknown } = {},
): Promise<T> {
  return (await apiRequestAmbEtag<T>(path, init)).data;
}

export async function apiRequestAmbEtag<T>(
  path: string,
  init: RequestInit & { json?: unknown } = {},
): Promise<{ data: T; etag: string | null }> {
  const { json, headers: h, ...resta } = init;
  const headers = await capcaleres(h);
  let body = resta.body;
  if (json !== undefined) {
    headers.set('Content-Type', 'application/json');
    body = JSON.stringify(json);
  }
  let response: Response;
  try {
    response = await fetch(`${config.apiBaseUrl}${path}`, { ...resta, headers, body });
  } catch {
    throw new ApiError(0, undefined);
  }
  if (response.status === 401) onNoAutenticat();
  if (!response.ok) {
    const error = (await response.json().catch(() => undefined)) as Partial<ErrorDto> | undefined;
    throw new ApiError(response.status, error);
  }
  const etag = response.headers.get('ETag');
  if (response.status === 204) return { data: undefined as T, etag };
  const tipus = response.headers.get('Content-Type') ?? '';
  const data = (tipus.includes('json') ? await response.json() : await response.text()) as T;
  return { data, etag };
}

/**
 * Petició multipart (`FormData`) amb la mateixa autenticació i gestió d'errors que `apiRequest`.
 * No es fixa `Content-Type`: el navegador hi posa el `boundary`.
 */
export function apiMultipart<T>(path: string, form: FormData, init: RequestInit = {}): Promise<T> {
  return apiRequest<T>(path, { method: 'POST', ...init, body: form });
}

/** Contingut binari autenticat (p. ex. una imatge del xat) com a `Blob`. */
export async function apiBlob(path: string): Promise<Blob> {
  const headers = await capcaleres({ Accept: 'image/*,*/*' });
  let response: Response;
  try {
    response = await fetch(`${config.apiBaseUrl}${path}`, { headers });
  } catch {
    throw new ApiError(0, undefined);
  }
  if (response.status === 401) onNoAutenticat();
  if (!response.ok) {
    const error = (await response.json().catch(() => undefined)) as Partial<ErrorDto> | undefined;
    throw new ApiError(response.status, error);
  }
  return response.blob();
}

export async function apiDescarrega(path: string, nomPerDefecte: string): Promise<void> {
  const headers = await capcaleres({ Accept: '*/*' });
  let response: Response;
  try {
    response = await fetch(`${config.apiBaseUrl}${path}`, { headers });
  } catch {
    throw new ApiError(0, undefined);
  }
  if (response.status === 401) onNoAutenticat();
  if (!response.ok) {
    const error = (await response.json().catch(() => undefined)) as Partial<ErrorDto> | undefined;
    throw new ApiError(response.status, error);
  }
  const disposicio = response.headers.get('Content-Disposition') ?? '';
  const nom = /filename="?([^";]+)"?/i.exec(disposicio)?.[1] ?? nomPerDefecte;
  const url = URL.createObjectURL(await response.blob());
  const enllac = document.createElement('a');
  enllac.href = url;
  enllac.download = nom;
  enllac.click();
  // Alguns navegadors inicien la descàrrega de manera asíncrona: es revoca més tard.
  setTimeout(() => URL.revokeObjectURL(url), 60_000);
}
